import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Modal } from '../Modal';
import { SentenceWithPhone } from '../SentenceWithPhone';
import { CodeStepBody } from './onboarding/CodeScreen';
import { PhoneStepBody } from './onboarding/PhoneScreen';
import { Typography } from './ui/Typography';
import { useCodeVerification, type CodeVerificationInput } from '../../lib/use-code-verification';
import { clearPendingVerification, readPendingVerification, savePendingVerification } from '../../lib/pending-verification';
import { useCodeWaitCarry, type CodeWaitCarry } from '../../lib/code-wait-carry';
import { useGenericBi } from '../../lib/use-generic-bi';
import { PHONE_VERIFY_PAGE } from '../../lib/use-phone-verification';
import { useTranslation } from '../../lib/i18n';
import { recipientNeedsPhone } from '../../lib/recipient-needs-phone';
import { VERIFY_CODE, VERIFY_CONNECT, VERIFY_NUMBER, VERIFY_PHONE } from '../../lib/phone-verify-steps';
import { useRecipient } from '../settings/shared/settings-queries';
import { VerifyWhatsappDialog } from './credits-widgets/VerifyWhatsappDialog';
import styles from './PhoneVerifyModal.module.css';

export { VERIFY_CODE, VERIFY_CONNECT, VERIFY_NUMBER, VERIFY_PHONE };

/**
 * Phone verification over the conversation.
 *
 * While no phone is on file, `?verify=phone` (the assistant's ask) and `?verify=connect` (the rail, the Connectors
 * page) open "Verify with WhatsApp" first, in its `connect` mode (2026-11-03): about half of the computer users the
 * daily-limit offer showed it to verified in seconds by sending one message. Its "Enter my number instead" goes to
 * `?verify=number`, the number step alone. With a phone on file they open the number step as before: the WhatsApp
 * route is fill-only, so a different number would only be refused (PHONE_CONFLICT).
 *
 * Which step is open lives in `?verify=phone|connect|number|code`, so the destination the assistant navigates to is
 * a URL like any other. What was SENT — the number, the verificationId — deliberately does not: it is
 * held here in memory and handed to the code step directly. The standalone `/onboarding/code` page
 * takes those in its query string, and an instrumented page publishes its own location, so that page
 * hands the number and the verificationId to every analytics tag on it (measured: Facebook, Google
 * Ads, DoubleClick, two Google endpoints, the GTM container and frog all received them).
 *
 * A reload still lands on the code step, because the sent code is kept for the tab in
 * `sessionStorage` (see `pending-verification`) — storage is not something an analytics tag reads, where
 * the location is something every one of them sends. When it genuinely is not there, the step falls back
 * to sending again rather than showing a code field with no number behind it.
 *
 * Closing strips the verify param and leaves the rest of the URL alone, so the conversation the user
 * was in stays open behind it.
 */
export function PhoneVerifyModal() {
  const router = useRouter();
  const { t } = useTranslation();
  const [sent, setSent] = useState<CodeVerificationInput['sent']>(undefined);
  const step = router.query.verify;
  // The step is in the URL, so the SERVER knows it too — but `Modal` portals to `document.body` and
  // renders nothing without a document. That asymmetry is a hydration mismatch: markup on the client,
  // none from the server. Waiting for the mount makes both passes agree on "nothing", and the modal
  // appears on the commit after.
  const [mounted, setMounted] = useState(false);
  // Same pass picks up a code already sent from this tab, which is what makes a reload land back on the
  // code step. It has to happen here rather than in the initial state for the same reason as the gate:
  // there is no `sessionStorage` on the server.
  useEffect(() => {
    setSent(readPendingVerification());
    setMounted(true);
  }, []);
  const open = mounted && (step === VERIFY_PHONE || step === VERIFY_CONNECT || step === VERIFY_NUMBER || step === VERIFY_CODE);
  // Shared with the rail and the onboarding-entry decision, so this read is usually already in the cache.
  const recipientQuery = useRecipient();
  // Whether this opening leads with "Verify with WhatsApp", decided ONCE per opening: the sheet re-reads the
  // recipient until the number lands, and re-deciding then would swap its done state for the number step.
  const needsPhone = recipientQuery.status === 'loading' ? null : recipientNeedsPhone(recipientQuery.data);
  const [whatsappFirst, setWhatsappFirst] = useState<boolean | null>(null);
  useEffect(() => {
    if (!open) setWhatsappFirst(null);
    else if (whatsappFirst === null && needsPhone !== null) setWhatsappFirst(needsPhone);
  }, [open, whatsappFirst, needsPhone]);
  // The code step's wait, carried to the number step when the same number is entered again. It belongs to
  // this opening: emptied whenever the dialog is not open, which also covers leaving by browser Back.
  const codeWait = useCodeWaitCarry(open);
  /**
   * The dialog reports itself opening, once per opening rather than per step: moving from the phone step to
   * the code step is progress through one dialog, not a second one.
   *
   * It is the only event that says the ask was actually PUT to someone. The assistant navigating here, the
   * user reopening it from a card, and a reload landing back on the code step all arrive the same way — as a
   * `?verify=` URL — so without this the funnel starts at the first keystroke in the number field and every
   * user who saw the dialog and closed it is indistinguishable from one who was never asked.
   */
  const { logModalActionBi } = useGenericBi('verify-phone');
  useEffect(() => {
    if (!open) return;
    logModalActionBi({ ...PHONE_VERIFY_PAGE, modal_name: 'verify_phone', action: 'open' });
    // Keyed on `open` alone: a step change inside the dialog must not re-report it as a new opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toStep = (next: string | undefined) => {
    const { verify, ...rest } = router.query;
    const query = next ? { ...rest, verify: next } : rest;
    void router.replace({ pathname: router.pathname, query }, undefined, { shallow: true });
  };

  // Every exit from the flow drops the stored code, so a number never outlives the step that sent it.
  const forget = () => {
    clearPendingVerification();
    setSent(undefined);
  };

  const close = () => {
    forget();
    toStep(undefined);
  };

  if (!open) {
    return null;
  }

  if (step === VERIFY_PHONE || step === VERIFY_CONNECT) {
    // Nothing until the recipient is read: the number step flashing in and then turning into the WhatsApp sheet
    // reads as the dialog changing its mind. A failed read decides false, the number step, which always works.
    if (whatsappFirst === null) return null;
    if (whatsappFirst) {
      return <VerifyWhatsappDialog mode={{ kind: 'connect' }} onClose={close} onUseNumber={() => toStep(VERIFY_NUMBER)} />;
    }
  }

  // `verify=code` with nothing sent means the code is genuinely gone — a different tab, or a store that
  // would not keep it — so it shows the phone step: re-sending is the honest recovery, where a code
  // field with no number behind it would verify against nothing.
  return step === VERIFY_CODE && sent ? (
    <CodeStepModal
      sent={sent}
      codeWait={codeWait}
      onClose={close}
      onUseAnotherNumber={() => {
        forget();
        // The number step itself: `phone`/`connect` would put the WhatsApp sheet back in front of the number field.
        toStep(VERIFY_NUMBER);
      }}
    />
  ) : (
    // Deliberately NOT on the 650 cap + `scrollBody` every other modal takes (Monday 12771405229): the
    // number step's country picker is an absolutely-positioned dropdown, and a scroll container anywhere
    // above it traps the list in a scroll box instead of letting it overlay (the same reason the shell's
    // full-screen variant adds no overflow). The two steps are a field and a button — neither comes near
    // 650 — so capping without a scroll would only risk clipping the picker.
    <Modal
      open
      onClose={close}
      size="minimum"
      fullScreenOnSmall

      labelledBy="verify-phone-title"
    >
      <Typography variant="h1" align="center" id="verify-phone-title" className={styles.title}>
        {t('onboarding.phone.connect.title')}
      </Typography>
      <Typography variant="p2" align="center" className={styles.subtitle}>
        {t('onboarding.phone.connect.subtitle')}
      </Typography>
      <PhoneStepBody
        codeWait={codeWait}
        onCodeSent={(next) => {
          savePendingVerification(next);
          setSent(next);
          toStep(VERIFY_CODE);
        }}
      />
    </Modal>
  );
}

/**
 * Its own component so the code step's hook mounts only while that step is open — the hook
 * auto-submits off the params it finds, so mounting it behind a closed modal would fire the verify
 * before the user ever saw the field.
 */
function CodeStepModal({
  sent,
  codeWait,
  onClose,
  onUseAnotherNumber,
}: {
  sent: CodeVerificationInput['sent'];
  codeWait: CodeWaitCarry;
  onClose: () => void;
  onUseAnotherNumber: () => void;
}) {
  const { t } = useTranslation();
  // Verified from inside a conversation, so the modal just closes: the number is on file and the
  // thread the user was in is still behind it, where the page's own success goes on to the home entry.
  const code = useCodeVerification({ sent, onVerified: onClose, onUseAnotherNumber, codeWait });
  const phone = code.phone || t('onboarding.code.yourNumber');

  return (
    // No 650 cap here either — the step is one code field, and "use a different number" returns to the
    // phone step in the same shell (see the note there).
    <Modal
      open
      onClose={onClose}
      size="minimum"
      fullScreenOnSmall

      labelledBy="verify-code-title"
    >
      <Typography variant="h1" align="center" id="verify-code-title" className={styles.title}>
        {t('onboarding.code.symphony.title')}
      </Typography>
      <Typography variant="p2" align="center" className={styles.subtitle}>
        <SentenceWithPhone line={t('onboarding.code.symphony.subtitle', { phone })} phone={phone} />
      </Typography>
      <CodeStepBody code={code} />
    </Modal>
  );
}
